import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import type { RootState } from '../../redux/store';
import type { ProductConfig } from './customizerConfig';
import { THEME } from './theme';
import ImageGallery from './gallery/ImageGallery';
import CustomizationPanel from './CustomizationPanel';
import { useTearSheet } from '../../hooks/useTearSheet';

interface ProductDetailViewProps {
  product: ProductConfig;
  onGenerate: () => void;
  onRegenerate?: (galleryIndex: number) => void;
}

const ProductDetailView: React.FC<ProductDetailViewProps> = ({ product, onGenerate, onRegenerate }) => {
  const { streamStatus, streamResults, regeneratingIndex } = useSelector((s: RootState) => s.customization);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const isStreaming = streamStatus === 'streaming';

  const tearSheet = useTearSheet({
    defaultDimensions: product.defaultDimensions,
  });

  useEffect(() => {
    tearSheet.reset(product.defaultDimensions);
  }, [product.id]);

  const galleryImages = useMemo(() => {
    let images: { url: string; label: string }[] = [];

    if (streamResults.length > 0) {
      images = streamResults.map((r) => ({ url: r.image_url, label: r.filename }));
    } else if (!isStreaming) {
      images = product.baseImages.map((img) => ({ url: img.path, label: img.filename }));
    }

    if (!isStreaming) {
      const tearSheetUrl = tearSheet.generatedImageUrl || product.tearSheetImage;
      images.push({
        url: tearSheetUrl,
        label: tearSheet.generatedImageUrl ? 'Generated Tear Sheet' : 'Tear Sheet',
      });
    }

    return images;
  }, [streamResults, isStreaming, product.baseImages, product.tearSheetImage, tearSheet.generatedImageUrl]);

  const getSourceImageUrl = useCallback((): string => {
    if (streamResults.length > 1) return streamResults[1].image_url;
    if (streamResults.length === 1) return streamResults[0].image_url;
    if (product.baseImages.length > 1) return product.baseImages[1].path;
    return product.baseImages[0].path;
  }, [streamResults, product.baseImages]);

  const handleGenerateTearSheet = useCallback(() => {
    const sourceUrl = getSourceImageUrl();
    tearSheet.generateFromUrl(sourceUrl);
  }, [getSourceImageUrl, tearSheet.generateFromUrl]);

  // Regenerate: check if gallery index maps to a stream result with a cache_hash
  const canRegenerateImage = useCallback((galleryIdx: number): boolean => {
    if (streamResults.length === 0) return false;
    // Stream results occupy indices 0..streamResults.length-1 in the gallery
    return galleryIdx >= 0 && galleryIdx < streamResults.length && !!streamResults[galleryIdx]?.cache_hash;
  }, [streamResults]);

  const handleRegenerate = useCallback((galleryIdx: number) => {
    if (onRegenerate) {
      onRegenerate(galleryIdx);
    }
  }, [onRegenerate]);

  useEffect(() => {
    if (tearSheet.generatedImageUrl && galleryImages.length > 0) {
      setGalleryIndex(galleryImages.length - 1);
    }
  }, [tearSheet.generatedImageUrl, galleryImages.length]);

  return (
    <div className="flex-1 flex gap-5 min-h-0">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-[3] min-w-0"
      >
        <ImageGallery
          images={galleryImages}
          isLoading={isStreaming}
          loadingCount={product.baseImages.length}
          activeIndex={galleryIndex}
          onIndexChange={setGalleryIndex}
          onRegenerate={handleRegenerate}
          isRegenerating={regeneratingIndex !== null}
          canRegenerate={canRegenerateImage}
          product={product}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="flex-[2] min-w-[340px] max-w-[440px] rounded-2xl overflow-hidden"
        style={{ background: THEME.bgCard, border: `1px solid ${THEME.border}` }}
      >
        <CustomizationPanel
          product={product}
          onGenerate={onGenerate}
          streamStatus={streamStatus}
          tearSheet={{
            dimensions: tearSheet.dimensions,
            dimensionsEdited: tearSheet.dimensionsEdited,
            isGenerating: tearSheet.isGenerating,
            error: tearSheet.error,
            generatedImageUrl: tearSheet.generatedImageUrl,
            onDimensionsChange: tearSheet.setDimensions,
            onGenerate: handleGenerateTearSheet,
          }}
        />
      </motion.div>
    </div>
  );
};

export default ProductDetailView;
